import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import * as baseActions from '../../actions/base';
import * as orgActions from '../../actions/org';

import ChoiceModal from '../UI/ChoiceModal';

import { listAllSnippets, snippetLabel, snippetsProjectId } from '../../lib/snippets';

/**
 * Asks which snippet to append to the file currently open, then appends it.
 *
 * Every Org file in the snippet repository is offered, not just the daystart
 * folders — a snippet is useful outside of starting a day too.
 */
const SnippetPicker = ({
  onClose,
  base,
  org,
  syncBackendClient,
  snippetsRepositoryUrl,
}) => {
  const [snippets, setSnippets] = useState(null);
  const [isInserting, setIsInserting] = useState(false);

  const projectId = snippetsProjectId(snippetsRepositoryUrl);

  useEffect(() => {
    let isCurrent = true;

    const load = async () => {
      try {
        const available = await listAllSnippets(syncBackendClient, projectId);
        if (isCurrent) setSnippets(available);
      } catch (error) {
        console.error('Could not list snippets:', error);
        base.setDisappearingLoadingMessage(`Could not list snippets: ${error.message}`, 4000);
        onClose();
      }
    };

    load();

    return () => {
      isCurrent = false;
    };
  }, [base, onClose, projectId, syncBackendClient]);

  const handleChoose = async (path) => {
    setIsInserting(true);

    try {
      const contents = await syncBackendClient.getSnippetContents(projectId, path);

      if (!`${contents || ''}`.trim()) {
        base.setDisappearingLoadingMessage(`${path} is empty.`, 3000);
        onClose();
        return;
      }

      org.insertSnippetAtEnd(contents);
      base.setDisappearingLoadingMessage(`Inserted ${snippetLabel(path)}.`, 2000);
      onClose();
    } catch (error) {
      console.error('Could not insert the snippet:', error);
      base.setDisappearingLoadingMessage(`Could not insert ${path}: ${error.message}`, 4000);
      onClose();
    }
  };

  // Still loading: the modal is already up, so show it as busy rather than
  // flashing an empty list.
  if (snippets === null) {
    return (
      <ChoiceModal
        title="Insert snippet"
        isBusy={true}
        busyMessage="Looking for snippets..."
        choices={[]}
        onChoose={() => {}}
        onCancel={onClose}
      />
    );
  }

  if (!snippets.length) {
    return (
      <ChoiceModal
        title="Insert snippet"
        description="No snippets found in the repository. Snippets are .org files, or files without an extension."
        choices={[]}
        onChoose={() => {}}
        onCancel={onClose}
      />
    );
  }

  return (
    <ChoiceModal
      title="Insert snippet"
      description="Appended to the end of this file."
      isBusy={isInserting}
      busyMessage="Inserting..."
      choices={snippets.map((snippet) => ({
        key: snippet.path,
        label: snippetLabel(snippet.path),
        description: snippet.path,
        icon: 'puzzle-piece',
      }))}
      onChoose={handleChoose}
      onCancel={onClose}
    />
  );
};

const mapStateToProps = (state) => ({
  syncBackendClient: state.syncBackend.get('client'),
  snippetsRepositoryUrl: state.base.get('snippetsRepositoryUrl') || '',
});

const mapDispatchToProps = (dispatch) => ({
  base: bindActionCreators(baseActions, dispatch),
  org: bindActionCreators(orgActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(SnippetPicker);
